import { BUSINESS_TIMEZONE, clockTime } from '../../lib/datetime'
import type { WorkspaceRender } from '../../lib/wstypes'

/**
 * Render history (01 §3.5 "Compare / history", 07 §1).
 *
 * A homeowner tries five vanities. Four of them are gone the moment the fifth renders unless
 * something keeps them — and "show me the one from two minutes ago" is the single most common
 * thing anyone asks of a tool like this. Cheap to keep (the renders already exist), and it is
 * what makes trying things feel free.
 */

interface Props {
  renders: WorkspaceRender[]
  activeId: string | null
  onSelect: (render: WorkspaceRender) => void
}

export function Filmstrip({ renders, activeId, onSelect }: Props) {
  const done = renders.filter((render) => render.status === 'done' && render.thumb_url)
  if (done.length === 0) return null

  return (
    <section aria-labelledby="history-heading" className="anim-rise mt-4">
      <h2 id="history-heading" className="font-mono text-xs uppercase tracking-[0.14em] text-muted">
        Previews
      </h2>
      <ul className="-mx-5 m-0 mt-2 flex list-none gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0">
        {done.map((render) => (
          <li key={render.id} className="shrink-0">
            <button
              type="button"
              onClick={() => onSelect(render)}
              aria-pressed={render.id === activeId}
              className={`block overflow-hidden rounded-btn border-2 transition-all duration-200 hover:-translate-y-0.5 ${
                render.id === activeId ? 'border-blue' : 'border-transparent hover:border-line-2'
              }`}
            >
              <img
                src={render.thumb_url ?? ''}
                alt={`Preview from ${clockTime(render.created_at, BUSINESS_TIMEZONE)}`}
                className="h-20 w-28 object-cover"
                loading="lazy"
              />
              {render.low_confidence && (
                <span className="block bg-sand px-1 py-0.5 text-center text-xs font-semibold text-ink">
                  may be imperfect
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
